import React, { useState, useEffect, useRef } from 'react';
import { api } from './shared/utils/api';

function getDeviceInfo() {
  const ua = navigator.userAgent;
  let browser = 'Unknown';
  if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'Chrome';
  else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
  else if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Edg')) browser = 'Edge';

  let os = 'Unknown';
  if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac')) os = 'macOS';
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('Linux')) os = 'Linux';

  return {
    context_browser: `${browser} / ${os}`,
    context_screen: `${window.innerWidth}×${window.innerHeight}`,
  };
}

const TYPE_OPTIONS = [
  { value: 'bug',     label: 'Something is broken' },
  { value: 'feature', label: 'Feature request' },
  { value: 'general', label: 'General feedback' },
];

function SQAvatar() {
  return (
    <div className="fb-sq-avatar" title="SQETrack Support">
      SQ
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="fb-typing">
      <span /><span /><span />
    </div>
  );
}

function FeedbackCase({ item, onResolved }) {
  const [expanded, setExpanded] = useState(false);
  const [messages, setMessages] = useState(null);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [msgError, setMsgError] = useState('');
  const [replying, setReplying] = useState(null); // null | typing | string
  const bottomRef = useRef(null);

  const load = async () => {
    if (messages !== null) return;
    const data = await api(`/api/feedback/${item.id}/messages`);
    setMessages(Array.isArray(data) ? data : []);
  };

  const toggle = () => {
    if (!expanded) load();
    setExpanded(e => !e);
  };

  useEffect(() => {
    if (expanded && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [expanded, messages, replying]);

  const sendMessage = async () => {
    if (!newMsg.trim()) return;
    setSending(true); setMsgError(''); setReplying('typing');
    try {
      const res = await api(`/api/feedback/${item.id}/message`, {
        method: 'POST',
        body: JSON.stringify({ content: newMsg.trim() }),
      });
      if (res.error) {
        setMsgError(res.error);
        setReplying(null);
      } else {
        setNewMsg('');
        setReplying(res.reply || null);
        // Refresh messages
        const data = await api(`/api/feedback/${item.id}/messages`);
        setMessages(Array.isArray(data) ? data : []);
        setReplying(null);
      }
    } catch { setMsgError('Message failed to send. Try again.'); setReplying(null); }
    finally { setSending(false); }
  };

  const resolve = async () => {
    await api(`/api/feedback/${item.id}/resolve`, { method: 'POST' });
    onResolved(item.id);
  };

  const isOpen = !item.resolved;

  return (
    <div className="fb-case">
      <div className="fb-case-header" onClick={toggle} style={{ cursor: 'pointer' }}>
        <div className="fb-case-left">
          <span className={`fb-case-status ${item.resolved ? 'resolved' : 'open'}`}>
            {item.resolved ? '✓ Resolved' : 'Open'}
          </span>
          <span className="fb-type-tag">{item.type || 'general'}</span>
          <span className="fb-case-preview">{(item.description || item.text || '').slice(0, 80)}{(item.description || '').length > 80 ? '…' : ''}</span>
        </div>
        <div className="fb-case-meta">
          <span className="fb-date">{item.created_at?.slice(0, 10)}</span>
          <span className="fb-case-toggle">{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {expanded && (
        <div className="fb-case-body">
          {/* Original message */}
          <div className="fb-conv-row fb-conv-row-user">
            <div className="fb-bubble fb-bubble-user">
              <div className="fb-bubble-meta">
                <span className="fb-type-tag">{item.type || 'general'}</span>
                <span className="fb-date">{item.created_at?.slice(0, 10)}</span>
              </div>
              <div className="fb-bubble-text">{item.description || item.text}</div>
            </div>
          </div>

          {/* Claude triage Q+A */}
          {item.followup_question && (
            <div className="fb-conv-row fb-conv-row-support">
              <SQAvatar />
              <div className="fb-bubble fb-bubble-admin">
                <div className="fb-bubble-meta">
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent)' }}>SQETrack Support</span>
                </div>
                <div className="fb-bubble-text">{item.followup_question}</div>
              </div>
            </div>
          )}
          {item.followup_answer && (
            <div className="fb-conv-row fb-conv-row-user">
              <div className="fb-bubble fb-bubble-user">
                <div className="fb-bubble-text">{item.followup_answer}</div>
              </div>
            </div>
          )}

          {/* feedback_messages thread */}
          {messages === null ? (
            <div style={{ padding: '8px 0', color: 'var(--muted)', fontSize: 12 }}>Loading</div>
          ) : messages.map(m => (
            m.sender === 'user' ? (
              <div key={m.id} className="fb-conv-row fb-conv-row-user">
                <div className="fb-bubble fb-bubble-user">
                  <div className="fb-bubble-text">{m.content}</div>
                </div>
              </div>
            ) : (
              <div key={m.id} className="fb-conv-row fb-conv-row-support">
                <SQAvatar />
                <div className="fb-bubble fb-bubble-admin">
                  <div className="fb-bubble-meta">
                    <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent)' }}>SQETrack Support</span>
                  </div>
                  <div className="fb-bubble-text">{m.content}</div>
                </div>
              </div>
            )
          ))}

          {/* Typing indicator */}
          {replying === 'typing' && (
            <div className="fb-conv-row fb-conv-row-support">
              <SQAvatar />
              <div className="fb-bubble fb-bubble-admin">
                <TypingIndicator />
              </div>
            </div>
          )}

          {/* Admin reply */}
          {item.admin_reply && (
            <div className="fb-conv-row fb-conv-row-support">
              <SQAvatar />
              <div className="fb-bubble fb-bubble-admin">
                <div className="fb-bubble-meta">
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent)' }}>SQETrack team</span>
                  {item.admin_replied_at && <span className="fb-date">{item.admin_replied_at.slice(0, 10)}</span>}
                </div>
                <div className="fb-bubble-text">{item.admin_reply}</div>
              </div>
            </div>
          )}

          <div ref={bottomRef} />

          {/* Message input — only for open cases */}
          {isOpen && (
            <div className="fb-case-reply">
              <textarea
                className="feedback-textarea"
                placeholder="Add a follow-up message"
                value={newMsg}
                onChange={e => setNewMsg(e.target.value)}
                rows={2}
              />
              {msgError && <div className="feedback-error">{msgError}</div>}
              <div className="feedback-input-row" style={{ marginTop: 8 }}>
                <button className="feedback-submit-btn btn-ghost" onClick={resolve}>
                  Mark resolved
                </button>
                <button className="feedback-submit-btn" onClick={sendMessage} disabled={sending || !newMsg.trim()}>
                  {sending ? 'Sending' : 'Send'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Feedback({ onViewed }) {
  const [phase, setPhase] = useState('list'); // 'list' | 'new' | 'conversation'
  const [fbType, setFbType] = useState('general');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [currentFbId, setCurrentFbId] = useState(null);
  const [currentText, setCurrentText] = useState('');
  const [triageQuestion, setTriageQuestion] = useState(null);
  const [triageLoading, setTriageLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [savingReply, setSavingReply] = useState(false);
  const [history, setHistory] = useState([]);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (onViewed) onViewed();
  }, []); // eslint-disable-line

  const loadHistory = () => {
    api('/api/feedback').then(data => {
      if (Array.isArray(data)) setHistory(data);
    });
  };

  useEffect(() => {
    loadHistory();
  }, [phase]);

  useEffect(() => {
    if (phase === 'conversation') {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [triageQuestion, phase]);

  const handleResolved = (id) => {
    setHistory(prev => prev.map(f => f.id === id ? { ...f, resolved: true } : f));
  };

  const submit = async () => {
    if (!description.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const deviceInfo = getDeviceInfo();
      const res = await api('/api/feedback', {
        method: 'POST',
        body: JSON.stringify({
          type: fbType,
          description: description.trim(),
          context_route: window.location.pathname,
          ...deviceInfo,
        }),
      });
      if (res.error) { setError(res.error); return; }
      setCurrentFbId(res.id);
      setCurrentText(description.trim());
      setTriageQuestion(null);
      setTriageLoading(true);
      setPhase('conversation');
      try {
        const tr = await api(`/api/feedback/${res.id}/triage`, { method: 'POST' });
        setTriageQuestion(tr.question || '');
      } catch {
        setTriageQuestion('');
      } finally {
        setTriageLoading(false);
      }
    } catch {
      setError('Feedback failed to send. Try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitReply = async () => {
    if (!replyText.trim()) { setPhase('list'); return; }
    setSavingReply(true);
    try {
      await api(`/api/feedback/${currentFbId}/followup`, {
        method: 'POST',
        body: JSON.stringify({ answer: replyText.trim() }),
      });
    } finally {
      setSavingReply(false);
      setPhase('list');
    }
  };

  const openItems = history.filter(f => !f.resolved);
  const resolvedItems = history.filter(f => f.resolved);

  return (
    <div className="feedback-page">
      <h1 className="page-heading">Feedback</h1>
      <div className="feedback-intro">
        <div className="feedback-title">Help improve SQETrack</div>
        <p className="feedback-desc">Tell us what you were doing, what happened, and what you expected. Specific details help us respond faster.</p>
      </div>

      {/* New feedback form */}
      {phase === 'new' && (
        <div className="feedback-form-card">
          <div className="feedback-type-row">
            {TYPE_OPTIONS.map(opt => (
              <label key={opt.value} className={`feedback-type-btn ${fbType === opt.value ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="fb_type"
                  value={opt.value}
                  checked={fbType === opt.value}
                  onChange={() => setFbType(opt.value)}
                  style={{ display: 'none' }}
                />
                {opt.label}
              </label>
            ))}
          </div>
          <textarea
            className="feedback-textarea"
            placeholder="What happened or what would you like to see?"
            value={description}
            onChange={e => setDescription(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit(); }}
            rows={5}
            autoFocus
          />
          {error && <div className="feedback-error">{error}</div>}
          <div className="feedback-input-row">
            <button className="feedback-submit-btn btn-ghost" onClick={() => setPhase('list')}>Cancel</button>
            <button
              className="feedback-submit-btn"
              onClick={submit}
              disabled={submitting || !description.trim()}
            >
              {submitting ? 'Sending' : 'Send feedback'}
            </button>
          </div>
          <p className="feedback-disclosure">SQETrack Support uses AI to help understand your feedback. Every submission is reviewed by a real person.</p>
        </div>
      )}

      {/* Conversation after submit */}
      {phase === 'conversation' && (
        <div className="feedback-form-card">
          <div className="fb-conv-thread">
            <div className="fb-conv-row fb-conv-row-user">
              <div className="fb-bubble fb-bubble-user">
                <div className="fb-bubble-meta">
                  <span className="fb-type-tag">{fbType}</span>
                  <span className="fb-date">Just now</span>
                </div>
                <div className="fb-bubble-text">{currentText}</div>
              </div>
            </div>

            <div className="fb-conv-row fb-conv-row-support">
              <SQAvatar />
              <div className="fb-bubble fb-bubble-admin">
                <div className="fb-bubble-meta">
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent)' }}>SQETrack Support</span>
                </div>
                {triageLoading ? (
                  <TypingIndicator />
                ) : triageQuestion ? (
                  <>
                    <div className="fb-bubble-text">{triageQuestion}</div>
                    <div style={{ marginTop: '0.75rem' }}>
                      <textarea
                        className="feedback-textarea"
                        placeholder="Your reply (optional)"
                        value={replyText}
                        onChange={e => setReplyText(e.target.value)}
                        rows={3}
                        autoFocus
                      />
                      <div className="feedback-input-row" style={{ marginTop: '0.5rem' }}>
                        <button className="feedback-submit-btn btn-ghost" onClick={() => setPhase('list')}>
                          Skip
                        </button>
                        <button className="feedback-submit-btn" onClick={submitReply} disabled={savingReply}>
                          {savingReply ? 'Saving' : 'Reply'}
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="fb-bubble-text">Logged for review. We will follow up if needed.</div>
                )}
              </div>
            </div>

            {!triageLoading && !triageQuestion && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                <button className="feedback-submit-btn" onClick={() => setPhase('list')}>Done</button>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
          <p className="feedback-disclosure" style={{ marginTop: '1rem' }}>SQETrack Support uses AI to help understand your feedback. Every submission is reviewed by a real person.</p>
        </div>
      )}

      {/* Case list */}
      {phase === 'list' && (
        <>
          <button className="feedback-submit-btn" style={{ marginBottom: 16 }} onClick={() => {
            setPhase('new');
            setFbType('general');
            setDescription('');
            setError(null);
          }}>
            + New feedback
          </button>

          {history.length === 0 && (
            <div style={{ color: 'var(--muted)', fontSize: 13, padding: '16px 0' }}>
              No feedback submitted yet. Use the button above to share your thoughts.
            </div>
          )}

          {openItems.length > 0 && (
            <div className="feedback-history">
              <div className="feedback-section-title" style={{ marginBottom: 8 }}>Open</div>
              {openItems.map(f => (
                <FeedbackCase key={f.id} item={f} onResolved={handleResolved} />
              ))}
            </div>
          )}

          {resolvedItems.length > 0 && (
            <div className="feedback-history" style={{ marginTop: openItems.length > 0 ? 20 : 0 }}>
              <div className="feedback-section-title" style={{ marginBottom: 8 }}>Resolved</div>
              {resolvedItems.map(f => (
                <FeedbackCase key={f.id} item={f} onResolved={handleResolved} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
